/**
 * Shared line / document totals for Superfaktura CRM (DocumentDetails, documents list).
 * Handles negative net lines (Zľava) and PostgREST string decimals.
 */

export const roundMoney = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

export function toMoneyNumber(v: unknown): number {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const s = String(v).trim().replace(/\s/g, "").replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function isLineGrossMissing(gross: number): boolean {
  return !Number.isFinite(gross) || gross === 0;
}

export function computeLineWithVatAmounts(
  unitPriceWithoutTaxRaw: unknown,
  quantityRaw: unknown,
  totalPriceFromApiRaw: unknown,
  taxRateRaw: unknown
): { totalWithVat: number; unitPriceWithVat: number } {
  const unitPriceWithoutTax = toMoneyNumber(unitPriceWithoutTaxRaw);
  const quantity = toMoneyNumber(quantityRaw);
  let totalPrice = toMoneyNumber(totalPriceFromApiRaw);
  const taxRate = toMoneyNumber(taxRateRaw);

  if (isLineGrossMissing(totalPrice) && unitPriceWithoutTax !== 0 && quantity > 0) {
    if (taxRate > 0) {
      totalPrice = roundMoney(unitPriceWithoutTax * quantity * (1 + taxRate / 100));
    } else {
      totalPrice = roundMoney(unitPriceWithoutTax * quantity);
    }
  } else {
    totalPrice = roundMoney(totalPrice);
  }
  let unitPriceWithVat = 0;
  if (quantity > 0 && totalPrice !== 0) {
    // Effektív bruttó egységár a kerekített sorösszegből (qty>1: több tized, hogy ne legyen ellentmondás a Spolu s DPH oszloppal)
    unitPriceWithVat =
      quantity !== 1 ? Math.round((totalPrice / quantity) * 10000) / 10000 : roundMoney(totalPrice / quantity);
  } else if (unitPriceWithoutTax !== 0) {
    unitPriceWithVat =
      taxRate > 0 ? roundMoney(unitPriceWithoutTax * (1 + taxRate / 100)) : unitPriceWithoutTax;
  }
  return { totalWithVat: totalPrice, unitPriceWithVat };
}

export function documentNotesIndicateNoVat(notes?: string | null): boolean {
  return !!(
    notes &&
    (notes.includes("Nie sme platiteľmi DPH") || notes.includes("platiteľmi DPH"))
  );
}

/**
 * Same dedupe key as DocumentDetails document_items Map — API/DB can return duplicate rows
 * (e.g. join glitch); first occurrence wins so list totals match the modal table.
 */
export function dedupeSuperfakturaLineItemsForTotals(items: any[]): any[] {
  const map = new Map<string, any>();
  for (const item of items) {
    const name = String(item.item_name ?? item.name ?? "").trim();
    const qty = toMoneyNumber(item.quantity);
    const unit = toMoneyNumber(item.unit_price);
    const key = `${name}_${qty}_${unit}`;
    if (!map.has(key)) {
      map.set(key, item);
    }
  }
  return Array.from(map.values());
}

/** Sum net (bez DPH) and gross (s DPH) from DB line items; null if no items. */
export function computeTotalsFromSuperfakturaLineItems(
  items: any[] | undefined | null,
  opts: {
    notes?: string | null;
    tax_rate?: number | null;
    extracted_financial_taxRate?: number | null;
  }
): { netTotal: number; grossTotal: number } | null {
  if (!items || !Array.isArray(items) || items.length === 0) return null;
  const uniqueItems = dedupeSuperfakturaLineItemsForTotals(items);
  if (uniqueItems.length === 0) return null;

  const documentHasNoVat = documentNotesIndicateNoVat(opts.notes ?? undefined);
  const fallbackRate = toMoneyNumber(opts.tax_rate ?? opts.extracted_financial_taxRate ?? 0);

  let netAgg = 0;
  let grossAgg = 0;
  for (const i of uniqueItems) {
    const u = toMoneyNumber(i.unit_price);
    const qtyRaw = toMoneyNumber(i.quantity);
    const q = qtyRaw > 0 ? qtyRaw : 1;
    const tr = documentHasNoVat
      ? 0
      : toMoneyNumber(
          i.tax_rate ?? i.taxRate ?? i.raw_data?.taxRate ?? i.raw_data?.tax ?? fallbackRate
        );
    const { totalWithVat } = computeLineWithVatAmounts(u, q, i.total_price, tr);
    netAgg += roundMoney(u * q);
    grossAgg += totalWithVat;
  }
  return { netTotal: roundMoney(netAgg), grossTotal: roundMoney(grossAgg) };
}
